/**
 * SA-1127: an email message reads as mail: a full-width block led by a
 * direction mark and SA-1089's header line, then the HTML frame or the text
 * body (SA-1093), then its files; a hairline separates blocks. It has no
 * footer: the header line carries the full time (R8). A mail sent from the
 * desk stores no sender address, so its line reads the full time alone.
 * Chat messages keep their bubbles (InboxConversationDetail's MessageBubble).
 */
import { useMemo, useState } from 'react'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { cn } from '@/utils'
import { Badge, Tip } from '@/components/ui'
import type { InboxMessage, InboxMissingFile } from '@/types/inbox'
import { ImageGallery } from './ImageGallery'
import { splitAttachments, viewerItems } from './mediaGallery'
import { InboxEmailBody } from './InboxEmailBody'
import { InboxEmailHtml } from './InboxEmailHtml'
import { htmlStateOf } from './emailHtml'
import { HTML_NOT_KEPT, HTML_NOT_KEPT_TOO_LARGE, SHOW_AS_HTML, SHOW_AS_TEXT } from './emailHtmlText'
import { fullTimestamp, messageHeader, placeholderOnly, stripSubject } from './emailThread'
import { EMAIL_RECEIVED, EMAIL_SENT } from './inboxText'
import { AttachmentRow, MissingFileLines } from './MessageFiles'
import { CHIP_TIP, chipName } from './triageText'

export interface EmailMessageProps {
  message: InboxMessage
  missing?: readonly InboxMissingFile[]
  /** SA-1089: the attachments stored as their own rows, shown under this body. */
  attachments?: readonly InboxMessage[]
  /** SA-1089: the thread header's subject, so a block repeats neither it nor its own line. */
  threadSubject?: string | null
  /** SA-1093, SA-1110: the rows this body's frame may cite by Content-ID. */
  cidRows?: readonly InboxMessage[]
  /** SA-1093: whether the thread lets the frame load remote images. */
  externalImages?: boolean
  /** SA-1295: the chip naming this email's triage row, and how to show the row. */
  triage?: { text: string; waiting: boolean; onShow: () => void }
}

/** SA-1300: the chip, one copy for both header lines. */
function TriageChip({ text, waiting, onShow }: { text: string; waiting: boolean; onShow: () => void }) {
  return (
    <Tip text={CHIP_TIP}>
      <Badge asChild variant={waiting ? 'warning-soft' : 'neutral'} className="ml-1.5 cursor-pointer px-1.5 py-0 text-[10px] align-middle">
        <button type="button" aria-label={chipName(text)} onClick={onShow}>{text} ›</button>
      </Badge>
    </Tip>
  )
}

export function EmailMessage({
  message, missing = [], attachments = [], threadSubject = null, cidRows = [], externalImages = false, triage,
}: EmailMessageProps) {
  // SA-1094: the set the viewer steps through: the joined attachments, else the message's own file.
  const set = useMemo(
    () => viewerItems(attachments.length > 0 ? attachments : [message]),
    [attachments, message],
  )
  const { images, rest } = useMemo(() => splitAttachments(attachments), [attachments])
  const viewerAt = (id: string) => {
    const index = set.findIndex((it) => it.messageId === id)
    return index >= 0 ? { items: set, index } : undefined
  }
  // SA-1093: "Show as text" flips this one block, not remembered (R3).
  const htmlState = htmlStateOf(message)
  const [asText, setAsText] = useState(false)
  const showFrame = htmlState === 'html' && !asText
  const header = messageHeader(message, threadSubject)
  const bodyText = stripSubject(message.content ?? '', threadSubject)
  const showText = Boolean(bodyText) && !placeholderOnly(message)
  const sent = !(message.senderType === 'customer' || message.role === 'user')
  const Mark = sent ? ArrowUpRight : ArrowDownLeft
  const ownMedia = (message.mediaType === 'image' || message.mediaType === 'document'
    || message.mediaType === 'audio' || message.mediaType === 'video') && Boolean(message.mediaUrl)

  return (
    <article data-testid="email-message" className="w-full border-b border-border pb-4 text-sm">
      <div className="flex items-start gap-2">
        <span
          role="img"
          aria-label={sent ? EMAIL_SENT : EMAIL_RECEIVED}
          className={cn(
            'mt-px flex size-[22px] shrink-0 items-center justify-center rounded-full',
            sent ? 'bg-info/10 text-info' : 'bg-muted text-muted-foreground',
          )}
        >
          <Mark className="size-3" aria-hidden="true" />
        </span>
        {header ? (
          <p data-testid="email-message-header" className="min-w-0 flex-1 pt-0.5 text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
            <span className="font-medium text-foreground">{header.sender}</span>
            {header.when && <span> · {header.when}</span>}
            {message.channelStatus && <span> · {message.channelStatus}</span>}
            {triage && <TriageChip {...triage} />}
            {header.subject && (
              <span data-testid="email-message-subject" className="block">Subject: {header.subject}</span>
            )}
          </p>
        ) : (
          <p className="min-w-0 flex-1 pt-0.5 text-[11px] text-muted-foreground">
            {fullTimestamp(message.timestamp)}
            {message.channelStatus && <span> · {message.channelStatus}</span>}
            {triage && <TriageChip {...triage} />}
          </p>
        )}
        {htmlState === 'html' && (
          <button
            type="button"
            onClick={() => setAsText((v) => !v)}
            className="shrink-0 pt-0.5 text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            {asText ? SHOW_AS_HTML : SHOW_AS_TEXT}
          </button>
        )}
      </div>
      <div className="mt-2 min-w-0">
        {ownMedia && (
          <AttachmentRow file={message} viewer={viewerAt(message.id)} />
        )}
        <MissingFileLines missing={missing} />
        {showFrame && (
          <InboxEmailHtml messageId={message.id} cidRows={cidRows} externalImages={externalImages} fallbackText={bodyText} />
        )}
        {!showFrame && showText && <InboxEmailBody text={bodyText} />}
        {htmlState === 'too_large' && (
          <p className="mt-1 text-[11px] text-muted-foreground italic">{HTML_NOT_KEPT_TOO_LARGE}</p>
        )}
        {htmlState === 'store_failed' && (
          <p className="mt-1 text-[11px] text-muted-foreground italic">{HTML_NOT_KEPT}</p>
        )}
        {attachments.length > 0 && (
          <div data-testid="email-attachments" className="mt-2 flex max-w-xl flex-col gap-1.5">
            {images.length > 1 ? (
              <ImageGallery images={images} viewer={set} />
            ) : (
              images.map((file) => <AttachmentRow key={file.id} file={file} viewer={viewerAt(file.id)} />)
            )}
            {rest.map((file) => <AttachmentRow key={file.id} file={file} viewer={viewerAt(file.id)} />)}
          </div>
        )}
      </div>
    </article>
  )
}
